'use client';

import { ExternalLink, Trophy } from 'lucide-react';
import type { i18n, TFunction } from 'i18next';
import type { CompanyFinancialsResponse, NaceCodeItem } from '@/lib/backend-api';
import { localizePathname, type Locale } from '@/lib/i18n-routes';
import { generateIndustryUrl } from '@/lib/industry-utils';
import type { TopEmployerRankLink } from '@/lib/seo-lists/types';
import { Badge } from '@/components/ui/badge';
import { LocaleLink } from '@/components/locale-link';

/** Top-Arbeitgeber rank link and NACE pills under the About text. The caller
 *  renders it only when there is at least one of them. */
export function ProfileIndustryTags({
  naceCodes,
  topEmployerRank,
  t,
  i18n,
}: {
  naceCodes: NaceCodeItem[];
  topEmployerRank: TopEmployerRankLink | null | undefined;
  t: TFunction;
  i18n: i18n;
}) {
  return (
    <div className="flex flex-wrap gap-2 mt-2">
      {topEmployerRank && (
        <LocaleLink
          href={topEmployerRank.href}
          title={topEmployerRank.hint}
          className="hover:opacity-80 transition-opacity"
        >
          <span className="inline-flex items-center gap-1 whitespace-normal text-left text-[10px] sm:text-xs text-foreground border border-border rounded-md px-1.5 py-0.5 bg-muted/30 dark:bg-muted/50 dark:border-border/70">
            <Trophy size={10} className="shrink-0 opacity-60" />
            <span>
              {t('companyDetail.topEmployerRank', {
                rank: new Intl.NumberFormat(i18n.language).format(topEmployerRank.rank),
                total: new Intl.NumberFormat(i18n.language).format(topEmployerRank.total),
                list: topEmployerRank.title,
              })}
            </span>
          </span>
        </LocaleLink>
      )}
      {naceCodes.map((code, idx) => (
        <LocaleLink
          key={idx}
          href={generateIndustryUrl(code.code, code.description)}
          className="hover:opacity-80 transition-opacity"
        >
          <span className="inline-flex items-center gap-1 whitespace-normal text-left text-[10px] sm:text-xs text-foreground border border-border rounded-md px-1.5 py-0.5 bg-muted/30 dark:bg-muted/50 dark:border-border/70">
            <span>
              {code.section
                ? `${code.section}.${code.code}`
                : code.code}{' '}
              - {code.description}
            </span>
            <ExternalLink size={10} className="shrink-0 opacity-60" />
          </span>
        </LocaleLink>
      ))}
    </div>
  );
}

/** Product / sub-sector tags (first 8), each a tag search. */
export function ProfileProductTags({
  products,
  locale,
  t,
}: {
  products: string[];
  locale: Locale;
  t: TFunction;
}) {
  return (
    <div className="space-y-3 pt-4 border-t">
      <h2 className="text-xs sm:text-sm font-bold uppercase tracking-wider text-muted-foreground">
        {t('companyDetail.productsAndSubSectors')}
      </h2>
      <div className="flex flex-wrap gap-2">
        {products.slice(0, 8).map((product, idx) => (
          <LocaleLink
            key={idx}
            href={`${localizePathname('/search', locale as Locale)}?tag=${encodeURIComponent(product)}`}
            className="hover:opacity-80 transition-opacity"
          >
            <Badge
              variant="secondary"
              className="cursor-pointer border border-border bg-muted/60 text-foreground dark:bg-muted/50 flex items-center gap-1"
            >
              {product}
              <ExternalLink size={10} className="shrink-0 opacity-60" />
            </Badge>
          </LocaleLink>
        ))}
      </div>
    </div>
  );
}

/** Opportunity and risk keywords (first 8 each), each a tag search. The
 *  caller renders it only when at least one list is non-empty. */
export function ProfileKeywordTags({
  financialsData,
  locale,
  t,
}: {
  financialsData: CompanyFinancialsResponse | null;
  locale: Locale;
  t: TFunction;
}) {
  return (
    <div className="space-y-3 pt-4 border-t">
      <h2 className="text-xs sm:text-sm font-bold uppercase tracking-wider text-muted-foreground">
        {t('companyDetail.risksAndOpportunities')}
      </h2>
      <div className="space-y-2">
        {(financialsData?.opportunity_keywords?.length ?? 0) > 0 && (
          <div className="flex flex-wrap gap-2">
            {financialsData?.opportunity_keywords
              ?.slice(0, 8)
              .map((keyword, idx) => (
                <LocaleLink
                  key={`opp-${idx}`}
                  href={`${localizePathname('/search', locale as Locale)}?tag=${encodeURIComponent(keyword)}`}
                  className="hover:opacity-80 transition-opacity"
                >
                  <Badge
                    variant="success"
                    appearance="light"
                    size="sm"
                    className="cursor-pointer flex items-center gap-1"
                  >
                    {keyword}
                    <ExternalLink
                      size={10}
                      className="shrink-0 opacity-60"
                    />
                  </Badge>
                </LocaleLink>
              ))}
          </div>
        )}
        {(financialsData?.risk_keywords?.length ?? 0) > 0 && (
          <div className="flex flex-wrap gap-2">
            {financialsData?.risk_keywords
              ?.slice(0, 8)
              .map((keyword, idx) => (
                <LocaleLink
                  key={`risk-${idx}`}
                  href={`${localizePathname('/search', locale as Locale)}?tag=${encodeURIComponent(keyword)}`}
                  className="hover:opacity-80 transition-opacity"
                >
                  <Badge
                    variant="destructive"
                    appearance="light"
                    size="sm"
                    className="cursor-pointer flex items-center gap-1"
                  >
                    {keyword}
                    <ExternalLink
                      size={10}
                      className="shrink-0 opacity-60"
                    />
                  </Badge>
                </LocaleLink>
              ))}
          </div>
        )}
      </div>
    </div>
  );
}
